import { ChartFacade } from '@mode/charts/contract';
import { ChartActions, fromChartSelectors } from '@mode/charts/domain';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS, isPresent } from '@mode/shared/util-js';
import * as angular from 'angular';
import produce from 'immer';
import * as _ from 'lodash';
import { keyBy } from 'lodash';
import { Observable, Subscription, combineLatest, merge as observableMerge } from 'rxjs';
import { filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { AccountFacade } from '@mode/accounts/contract';
import { DeprecatedApiService, QueryResource } from '@mode/legacy/data-access-model-js';
import { ModelState, QueryState, TableState } from '@mode/legacy/types-app-state';
import { ofType } from '@mode/legacy/util-domain';
import { Logger } from '@mode/shared/contract-common';
import { ToastService } from '@mode/shared/util-toast';
import { Actions } from '~/actions';
import { ReportActionType, ReportUpdateStrategy } from '~/actions/report-actions';
import { ReportLayoutActions } from '~/actions/report-layout.actions';
import {
  CHART_OPTION_TYPES,
  CHART_OPTION_TYPES_WITH_KEY,
  CHART_TYPES,
  ChartOptionDefinition,
  DATA_LIMIT,
  EMPTY_LEGACY_CHART,
  EMPTY_VEGAS_CHART,
} from '~/common/constants';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { AppStore } from '~/types/main.types';
import { RouterStoreService } from './router-store-service';

export interface EmptyChartOptions {
  queryToken: string;
  holdView?: boolean;
  position?: number;
  defaultType?: string;
  version?: number;
  color_palette_token?: string;
}

export class ReportSaveService {
  static $inject = [
    '$rootScope',
    '$log',
    '$timeout',
    'ApiService',
    'ChartSanitizerService',
    'ChartInterpolaterService',
    'ChartModel',
    'PythonVisualizationModel',
    'ToastService',
    'RxStoreService',
    'actions$',
    'AccountFacade',
    'ChartFacade',
    'RouterStoreService',
  ];

  public oldReport = null;
  public report = null;
  public charts: ChartTypes.CoreChart[] = [];
  public tables: TableState[] = [];
  public pythonCells = [];
  public queries: _.Dictionary<any> = {};
  public name = null;
  public description = '';
  public space = null;
  private editorDirtySubscriber: Subscription;
  private editorQueriesSubscriber: Subscription;
  private editorIsDirty = false;
  public storedQueries: { [token: string]: QueryState } = {};
  private models: { [token: string]: Readonly<ModelState> };

  chartOptions = { types: keyBy(CHART_OPTION_TYPES.map(optionTypeToChartOption), 'type') };

  constructor(
    private $rootScope,
    private $log: Logger,
    private $timeout,
    private api: DeprecatedApiService,
    private ChartSanitizerService,
    private ChartInterpolaterService,
    private ChartModel,
    private PythonVisualizationModel,
    private alertService: ToastService,
    private rxStore: AppStore,
    private actions$: Observable<Actions>,
    private accountFacade: AccountFacade,
    private chartFacade: ChartFacade,
    private routerStoreService: RouterStoreService
  ) {
    const isDatasetRoute = this.routerStoreService.isDatasetsRoute();
    this.editorDirtySubscriber = rxStore
      .select(SelectedReportSelectors.createIsDirtySelector(isDatasetRoute))
      .subscribe((isDirty) => {
        $rootScope.$evalAsync(() => (this.editorIsDirty = isDirty));
      });

    this.editorQueriesSubscriber = rxStore.select('client', 'selectedReport', 'queries').subscribe((queries) => {
      this.storedQueries = queries.byKey.toObject();
    });

    // Maintain old chart storage for compatibility
    this.rxStore
      .select(fromChartSelectors.selectPersistableTokens)
      .pipe(
        switchMap((tokens) =>
          observableMerge(
            ...tokens.map((token) => this.rxStore.select(fromChartSelectors.createCoreChartSelector(token))).toArray()
          )
        )
      )
      .subscribe((chart) => {
        // Skip this for exploration visualizations.
        if (chart.token === FLAMINGO_RESERVED_REFS.Exploration) {
          return;
        }

        const index = this.charts.findIndex((c) => c.token === chart.token);
        if (index >= 0) {
          this.charts[index] = chart;
        } else {
          this.charts.push(chart);
        }
      });

    this.rxStore.select(SelectedReportSelectors.getChartModels).subscribe((models) => {
      this.models = models._byKey.toJSON();
    });

    // Maintain old table storage for compatibility
    this.rxStore
      .select('client', 'selectedReport', 'tablesByQuery')
      .pipe(
        switchMap((byQuery) =>
          observableMerge(
            ...byQuery
              .map((tableTokens) => tableTokens.first(null))
              .filter((t) => t != null)
              .map((token) => this.rxStore.select(SelectedReportSelectors.createTableSelector(token)))
              .toSet()
              .toArray()
          )
        ),
        filter((table) => table != null)
      )
      .subscribe((table) => {
        const index = this.tables.findIndex((t) => t.token === table.token);
        if (index >= 0) {
          this.tables[index] = table;
        } else {
          this.tables.push(table);
        }
      });

    this.actions$.pipe(ofType(ReportLayoutActions.Type.CreateChart)).subscribe((action) => {
      this.createEmptyChart({
        queryToken: action.queryToken,
        position: action.position,
        version: 2,
      });
    });

    this.actions$
      .pipe(
        ofType(ReportLayoutActions.Type.CloneChart),
        switchMap((action) =>
          this.chartFacade.createChartVersionFromVizToken(action.token).pipe(
            filter(isPresent),
            first(),
            map((version) => ({ action, version }))
          )
        )
      )
      .subscribe(({ action, version }) => {
        if (version === 2) {
          this.chartFacade.duplicateChartInBuilder(action.token);
        } else {
          this.cloneChart({
            token: action.token,
            position: action.position,
            holdView: true,
          });
        }
      });

    this.actions$.pipe(ofType(ReportActionType.UpdateReportSettings)).subscribe((action) => {
      const settings = {
        'report.full_width': action.payload.full_width ?? this.report.full_width,
        'report.manual_run_disabled': this.report.manualRunDisabled,
        'report.drill_anywhere_enabled': this.report.drillAnywhereEnabled,
        'report.theme': action.payload.theme ?? this.report.theme,
        includes: ['report_theme'],
      };
      this.report
        .saveReportSettings(settings)
        .then(
          (report: any) => {
            this.rxStore.dispatch({
              type: ReportActionType.UpdateReportSuccess,
              payload: {
                report: report._resource,
                strategy: ReportUpdateStrategy.Silent,
              },
            });
            this.$rootScope.$broadcast('preview.update', {
              updateSettings: { full_width: report.full_width, theme: report.localTheme },
            });
          },
          () => {}
        )
        .catch((err) => this.alertService.error('Could not save report settings'));
    });

    /**
     * Chart creation effect
     */
    this.actions$
      .pipe(
        ofType(ChartActions.createChart),
        withLatestFrom(
          this.accountFacade.defaultCategoricalPalette$,
          this.accountFacade.defaultDivergentPalette$,
          this.rxStore.select(SelectedReportSelectors.getQueries),
          (action, defaultCategorical, defaultDivergent, queries) => {
            const palette =
              action.chartType === 'bigValue' || action.chartType === 'bigNumber'
                ? defaultDivergent
                : defaultCategorical;

            return {
              action,
              palette,
              query: queries.getRecord(action.queryToken),
            };
          }
        ),
        filter(({ query }) => query != null),
        switchMap(({ action, palette, query }) => {
          const payload: Pick<
            ChartTypes.ChartResource,
            'view' | 'view_vegas' | 'view_version' | 'color_palette_token'
          > = {
            color_palette_token: palette.token,
            view_version: action.viewVersion || 1,
            view: null,
            view_vegas: null,
          };

          if (action.viewVersion === 2) {
            const chartType = (action.chartType as ChartTypes.ChartType) || 'table';
            payload.view_vegas = {
              ...EMPTY_VEGAS_CHART,
              chartType,
              encoding: EncodingHelpers.initEncoding(chartType).encoding,
              title: null,
              description: null,
              caption: null,
              fieldFormats: {},
              fieldColors: {},
              facetSizes: {},
              isAutoUpdateDisabled: false,
            };

            // Denormalize color palette to chart format
            payload.view_vegas.format = produce(payload.view_vegas.format, (draft) => {
              (draft.colors.theme = palette.token), (draft.colors.map = {});
              draft.colors.custom = palette.value.split(',');
            });
          } else {
            const chartType = (action.chartType as ChartTypes.V1ChartType) || 'line';
            const view = angular.copy(EMPTY_LEGACY_CHART);
            if (this.models[action.queryToken].count <= DATA_LIMIT) {
              const types = this.ChartInterpolaterService.interpolate(
                view,
                this.models[action.queryToken],
                this.chartOptions
              );
              this.ChartSanitizerService.sanitize(view, this.models[action.queryToken]);

              if (
                (!view.selectedChart && types.length) ||
                (types.length && !_.find(types, { type: view.selectedChart }))
              ) {
                view.selectedChart = types[0].type;
                view.chartIcon = types[0].icon;
              }
            }

            if (CHART_TYPES.indexOf(chartType) >= 0) {
              // overwrite interpolated selected chart with provided type
              view.selectedChart = chartType;
              view.chartIcon = CHART_OPTION_TYPES_WITH_KEY[chartType].icon;

              if (chartType === 'bigNumber') {
                view.bigNumber = {
                  ...view.bigNumber,
                  colors: palette.value.split(','),
                };
              }
            }

            payload.view = view;
          }

          return this.createChart(
            query.resource,
            payload.view,
            payload.view_vegas,
            payload.view_version,
            palette.token
          );
        })
      )
      .subscribe((action) => this.rxStore.dispatch(action));

    /**
     * Chart clone effect
     */
    this.actions$
      .pipe(
        ofType(ChartActions.cloneChart),
        switchMap((action) =>
          combineLatest(
            this.rxStore.selectNoEmpty(fromChartSelectors.createChartResourceSelector(action.token)),
            this.rxStore.selectNoEmpty(fromChartSelectors.createQueryTokenSelector(action.token)),
            this.rxStore.selectNoEmpty(SelectedReportSelectors.getQueries)
          ).pipe(first())
        ),
        switchMap(([resource, queryToken, queries]) =>
          this.createChart(
            queries.getRecord(queryToken).resource,
            resource.view,
            resource.view_vegas,
            resource.view_version,
            resource.color_palette_token
          )
        )
      )
      .subscribe((action) => this.rxStore.dispatch(action));
  }

  createChart(
    queryResource: QueryResource,
    view: ChartTypes.ChartResource['view'],
    viewVegas: ChartTypes.ChartResource['view_vegas'],
    version: ChartTypes.ChartResource['view_version'],
    colorPaletteToken: string
  ) {
    return this.api
      .create(queryResource, 'new_chart', {
        'chart.view': JSON.stringify(view || {}),
        'chart.view_vegas': JSON.stringify(viewVegas || {}),
        'chart.view_version': version,
        'chart.color_palette_token': colorPaletteToken,
        includes: ['color_palette'],
      })
      .then((res: ChartTypes.ChartResource) =>
        ChartActions.createChartSuccess({
          payload: res,
          queryToken: queryResource.token,
        })
      )
      .catch((err) =>
        ChartActions.createChartFailure({
          error: err,
        })
      );
  }

  reportHasChanged() {
    if (!this.oldReport || !this.report) {
      return false;
    }

    return this.editorIsDirty;
  }

  copyReport(report) {
    this.oldReport = {
      name: report.name,
      description: report.description,
      layout: report.layout,
      table_view: angular.copy(report.table_view),
    };
    this.description = report.description;
  }

  updateReport(report) {
    if (this.oldReport) {
      this.oldReport.name = report.name;
      this.oldReport.description = report.description;
      this.oldReport.layout = report.layout;
      this.oldReport.table_view = angular.copy(report.table_view);
    }
  }

  updateSpace(space) {
    this.space = space;
  }

  removeChart(token) {
    _.remove(this.charts, { token: token });
  }

  chartHasChanged(chart: ChartTypes.CoreChart): boolean {
    // This is only useful for checking V1 chart has changed
    return (
      chart.version === 1 &&
      (chart.v1View.selectedChart !== chart.resource.view.selectedChart ||
        chart.v1View.chartIcon !== chart.resource.view.chartIcon ||
        !angular.equals(chart.v1View[chart.v1View.selectedChart], chart.resource.view[chart.v1View.selectedChart]))
    );
  }

  removeTable(token) {
    _.remove(this.tables, { token: token });
  }

  tableHasChanged(table: TableState): boolean {
    return !angular.equals(table.currentView, table.resource.view);
  }

  removeQuery(token) {
    delete this.queries[token];
  }

  getVizById(id) {
    if (!id || id.split('_').length !== 2) {
      return;
    }
    const type = id.split('_')[0];
    const token = id.split('_')[1];
    switch (type) {
      case 'table':
        return _.find(this.tables, { token: token });
      case 'chart':
        return _.find(this.charts, { token: token });
      case 'python':
        return _.find(this.pythonCells, { token: token });
    }
  }

  getChartById(id) {
    if (id) {
      id = id.replace('chart_', '');
    }
    return this.charts.find((c) => c.token === id);
  }

  getTableById(id) {
    if (id) {
      id = id.replace('table_', '');
    }
    if (id && _.some(this.tables, { token: id })) {
      return _.find(this.tables, { token: id });
    }
  }

  getQueryById(id) {
    let query;
    if (id) {
      id = id.replace('query_', '');
    }

    return this.storedQueries[id];
  }

  setCharts(charts) {
    this.charts = charts;
  }

  setTables(tables) {
    this.tables = tables;
  }

  cloneChart(data: { token: string; holdView?: boolean; position?: number }) {
    this.rxStore.dispatch(
      ChartActions.cloneChart({
        token: data.token,
        holdView: data.holdView,
        position: data.position,
      })
    );
  }

  createEmptyChart({
    queryToken: query,
    holdView = false,
    position,
    defaultType,
    color_palette_token,
    version = 1,
  }: EmptyChartOptions) {
    this.dispatchCreateChart({
      query,
      holdView,
      position,
      defaultType,
      version,
      color_palette_token,
    });
  }

  dispatchCreateChart(data) {
    this.rxStore.dispatch(
      ChartActions.createChart({
        queryToken: data.query,
        viewVersion: data.version,
        chartType: data.defaultType,
      })
    );
  }
}

function optionTypeToChartOption(option: ChartOptionDefinition) {
  return {
    name: option.name,
    type: option.id,
    icon: option.icon,
  };
}
